import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { AuditLogFilter, AuditLogStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

export const DEFAULT_AUDIT_LOG_FILTER: AuditLogFilter = { page: 1, limit: 20 };

export const initialState: AuditLogStateType = {
  results: [],
  total: 0,
  filter: { ...DEFAULT_AUDIT_LOG_FILTER },
  filterOptions: { actions: [], entityTypes: [], actors: [] },
  listError: null,
  detail: null,
  detailNotFound: false,
  detailError: null,
};

// The api's own "missing row" status -- `AUDIT_LOG_NOT_FOUND` (404). Any OTHER failure code
// (403/500/network) is a real error, not "this row doesn't exist", and gets the distinct
// detailError state instead of detailNotFound.
const NOT_FOUND_STATUS = 404;

const reducer = createReducer<AuditLogStateType, Action>(initialState)
  // Page 1 REPLACES `results` (a filter change or the initial load); any later page APPENDS --
  // mirrors ProjectListContainer's reducer, adapted for the api's 1-indexed `page` (Project's own
  // is 0-indexed and keys off `is_clear` computed in its saga instead).
  .handleAction(actions.getAuditLogs.success, (state, action) =>
    produce(state, (nextState) => {
      const { results, total } = action.payload;
      nextState.results = state.filter.page === 1 ? [...results] : [...nextState.results, ...results];
      nextState.total = total;
      nextState.listError = null;
    }),
  )
  .handleAction(actions.getAuditLogs.failure, (state, action) =>
    produce(state, (nextState) => {
      // `results`/`total` are deliberately left untouched -- the rows already on screen stay up,
      // Retry re-requests the same (unchanged) filter rather than this reducer clearing anything.
      nextState.listError = action.payload;
    }),
  )
  .handleAction(actions.getAuditLogFilters.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.filterOptions = action.payload;
    }),
  )
  .handleAction(actions.getAuditLogDetail.request, (state) =>
    produce(state, (nextState) => {
      nextState.detail = null;
      nextState.detailNotFound = false;
      nextState.detailError = null;
    }),
  )
  .handleAction(actions.getAuditLogDetail.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.detail = action.payload;
      nextState.detailNotFound = false;
      nextState.detailError = null;
    }),
  )
  .handleAction(actions.getAuditLogDetail.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.detail = null;
      const isNotFound = action.payload.code === NOT_FOUND_STATUS;
      nextState.detailNotFound = isNotFound;
      nextState.detailError = isNotFound ? null : action.payload;
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload;
    }),
  )
  .handleAction(actions.clearAuditLogs, (state) =>
    produce(state, (nextState) => {
      nextState.results = [];
      nextState.total = 0;
      nextState.listError = null;
    }),
  )
  .handleAction(actions.clearDetail, (state) =>
    produce(state, (nextState) => {
      nextState.detail = null;
      nextState.detailNotFound = false;
      nextState.detailError = null;
    }),
  );

export { reducer as AuditLogReducer };
